import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  THEME_PRIMARY,
  THEME_PRIMARY_LIGHT,
  THEME_PRIMARY_TINT,
} from "../bookingTheme";
import CurrentVehicleSummaryCard from "./SummaryCard";
import {
  formatCurrentVehicleDetailValue,
  type CurrentBookingVehicleSummary,
} from "./summaryUtils";
import YourSelectionVehicleDetailRows, {
  type YourSelectionVehicleDetailStrings,
} from "./SelectionRows";
import SearchableSelectInput from "../../../components/forms/SearchableSelectInput";

export type CurrentBookingPlanContext = CurrentBookingVehicleSummary & {
  planName: string;
};

export interface PlanOption {
  id: string | number;
  name: string;
  duration?: string;
  price?: string;
  isCustom?: boolean;
  /** Optional badge text (e.g. "N P" for New/Popular) shown on card */
  badge?: string;
}

export interface CostBreakdownItem {
  label: string;
  value: string;
  message?: string;
  isDiscount?: boolean;
}

const REFUNDABLE_ROW_LABEL = "Refundable amount";

function parseTotalPayableAmount(display: string): number {
  const n =
    Number.parseFloat(String(display || "").replaceAll(/[₹,\s]/g, "")) || 0;
  return Number.isFinite(n) ? n : 0;
}

function parseRupeeDisplayValue(display: string | undefined): number {
  return parseTotalPayableAmount(display ?? "₹0");
}

/**
 * True when API breakdown shows ₹0 for plan, processing, deposit and total payable is ₹0.
 * Both Cash and Online stay enabled (parity with edit Select Plan).
 */
function isFullyZeroCostScenario(
  items: CostBreakdownItem[],
  totalPayableAmount: number,
): boolean {
  if (totalPayableAmount !== 0) return false;
  if (!items.some((i) => i.label === "Plan price")) return false;
  const plan = items.find((i) => i.label === "Plan price");
  const proc = items.find((i) => i.label === "Processing fees");
  const dep = items.find((i) => i.label === "Deposit amount");
  if (parseRupeeDisplayValue(plan?.value) !== 0) return false;
  if (parseRupeeDisplayValue(proc?.value) !== 0) return false;
  if (parseRupeeDisplayValue(dep?.value) !== 0) return false;
  return true;
}

/* TODO: Re-enable with Custom Plan card + dialog in this file (restore imports: dayjs/Dayjs + MUI DatePicker/LocalizationProvider/AdapterDayjs).
function formatCustomPlanDateDisplay(dateValue: string): string {
  const d = dayjs(dateValue);
  if (!d.isValid()) return dateValue;
  return d.format("DD/MM/YYYY");
}

const CUSTOM_PLAN_DAY_OFFSETS = [7, 14, 28] as const;

function customPlanAllowedToDates(from: Dayjs | null): Dayjs[] {
  if (!from || !from.isValid()) return [];
  const start = from.startOf("day");
  return CUSTOM_PLAN_DAY_OFFSETS.map((n) => start.add(n, "day").startOf("day"));
}

function isCustomPlanValidToDate(from: Dayjs, to: Dayjs): boolean {
  const start = from.startOf("day");
  const t = to.startOf("day");
  return CUSTOM_PLAN_DAY_OFFSETS.some((n) => start.add(n, "day").isSame(t, "day"));
}
*/

/** Parse formatted ₹ string (e.g. from API-driven breakdown); negative → show 0 in UI */
function displayCostBreakdownValue(item: CostBreakdownItem): string {
  if (item.label !== REFUNDABLE_ROW_LABEL) return item.value;
  const cleaned = String(item.value || "")
    .replaceAll("₹", "")
    .replaceAll(",", "")
    .trim();
  const n = Number.parseFloat(cleaned);
  if (!Number.isFinite(n) || n >= 0) return item.value;
  return `₹${Number(0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
}

function parseOnboardingAmount(value: string): number {
  const n = Number.parseFloat(String(value ?? "").trim());
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function formatOnboardingRs(amount: number): string {
  return `₹${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const ONBOARDING_PROCESSING_LABEL = "Processing fees";
const ONBOARDING_DEPOSIT_LABEL = "Deposit amount";
const CASH_HANDLING_LABEL = "Cash Handling Fees";
const WAIVED_PROCESSING_LABEL = "Waived processing amount";
const WAIVED_DEPOSIT_LABEL = "Waived deposit amount";
const WAIVED_CASH_HANDLING_LABEL = "Waived cash handling amount";

function shouldShowOnboardingPreviousRow(
  previous: number,
  current: number,
): boolean {
  return previous > 0 && previous != current;
}

function isFeeAmountChanged(baseline: number, current: number): boolean {
  return shouldShowOnboardingPreviousRow(baseline, current);
}

function getFeeDisplayLabel(
  canonicalLabel: string,
  waivedLabel: string,
  baseline: number,
  current: number,
): string {
  return isFeeAmountChanged(baseline, current) ? waivedLabel : canonicalLabel;
}

interface SelectPlanStepProps {
  plans: PlanOption[];
  selectedPlanId: string | number | null;
  onSelectPlan: (id: string | number) => void;
  couponCode: string;
  onCouponChange: (value: string) => void;
  /** Parent (`CreateBookingWizard`) calls CheckCouponValidity; snackbar shows API `message` and hides after 3s. */
  onApplyCoupon: () => void;
  additionalFees: string;
  onAdditionalFeesChange: (value: string) => void;
  paymentMethod: "cash" | "online";
  onPaymentMethodChange: (value: "cash" | "online") => void;
  costBreakdown: CostBreakdownItem[];
  totalPayable: string;
  /** True while GetCostBreakdownDifference (+ cash settings) is in flight */
  costBreakdownLoading?: boolean;
  loading?: boolean;
  /** From GetBookingById — current plan & vehicle context */
  currentBookingContext?: CurrentBookingPlanContext | null;
  /** Wizard hub / model — same as other steps */
  selectedHubName?: string | null;
  selectedModelName?: string | null;
  /** Hub vehicle chosen in step 2 — under "Your selection" */
  yourSelectionVehicleDetails?: YourSelectionVehicleDetailStrings | null;
  /** Matches payload new plan dates (today → inclusive end) */
  newPlanWindowDisplay?: { start: string; end: string } | null;
  onboardingProcessingFees: string;
  onboardingDepositAmount: string;
  /** Plan defaults before onboarding fee edits — used for waived / previous amounts. */
  onboardingBaselineProcessingFees: string;
  onboardingBaselineDepositAmount: string;
  onboardingRemarks: string;
  onOnboardingFeeChange: (
    processingFees: string,
    depositAmount: string,
    remarks: string,
  ) => void;
  /** Numeric string; synced with Cash Handling Fees row when plan loads or user edits */
  cashHandlingFees: string;
  /** System default cash charge — used for waived label and previous amount. */
  cashHandlingBaselineFees: string;
  onCashHandlingFeesChange: (value: string) => void;
  /** From `GetPromoCodesByUserId` — coupon names for searchable dropdown */
  promoCodeOptions?: { label: string; value: string }[];
  promoCodesLoading?: boolean;
  /** True while `CheckCouponValidity` is in flight */
  couponApplyLoading?: boolean;
}

const SelectPlanStep: React.FC<SelectPlanStepProps> = ({
  plans,
  selectedPlanId,
  onSelectPlan,
  couponCode,
  onCouponChange,
  onApplyCoupon,
  additionalFees,
  onAdditionalFeesChange,
  paymentMethod,
  onPaymentMethodChange,
  costBreakdown,
  totalPayable,
  costBreakdownLoading = false,
  loading = false,
  currentBookingContext = null,
  selectedHubName,
  selectedModelName,
  yourSelectionVehicleDetails = null,
  newPlanWindowDisplay = null,
  onboardingProcessingFees,
  onboardingDepositAmount,
  onboardingBaselineProcessingFees,
  onboardingBaselineDepositAmount,
  onboardingRemarks,
  onOnboardingFeeChange,
  cashHandlingFees,
  cashHandlingBaselineFees,
  onCashHandlingFeesChange,
  promoCodeOptions = [],
  promoCodesLoading = false,
  couponApplyLoading = false,
}) => {
  const totalPayableAmount = parseTotalPayableAmount(totalPayable);
  const fullyZeroTotals = isFullyZeroCostScenario(
    costBreakdown,
    totalPayableAmount,
  );
  /** Same race as edit Select Plan: first paint can be ₹0 + [] before parent effect sets loading. */
  const awaitingCostBreakdown =
    costBreakdownLoading ||
    (costBreakdown.length === 0 && totalPayableAmount <= 0);
  const paymentOptionsDisabled =
    !awaitingCostBreakdown && totalPayableAmount <= 0;
  const onlinePaymentEnabled =
    !paymentOptionsDisabled &&
    (awaitingCostBreakdown || totalPayableAmount > 0 || fullyZeroTotals);

  const hubLabel = selectedHubName?.trim() || "";
  const modelLabel = selectedModelName?.trim() || "";
  const selectedPlanLabel = useMemo(() => {
    if (selectedPlanId == null || selectedPlanId === "") return "";
    const p = plans.find((pl) => String(pl.id) === String(selectedPlanId));
    return (p?.name ?? "").trim();
  }, [plans, selectedPlanId]);

  const couponSectionEnabled =
    selectedPlanId != null &&
    selectedPlanId !== "" &&
    String(selectedPlanId).trim() !== "";

  const showYourSelection =
    Boolean(hubLabel) || Boolean(modelLabel) || Boolean(selectedPlanLabel);

  const selectionVehicleDetails: YourSelectionVehicleDetailStrings =
    yourSelectionVehicleDetails ?? {
      vehicleName: "",
      vehicleNum: "",
      registrationNumber: "",
      speed: "",
      range: "",
    };
  const showCurrentVehicle = Boolean(currentBookingContext);

  const selectionSummaryRow = (label: string, value: string) => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(108px, 38%) minmax(0, 1fr)",
          sm: "118px minmax(0, 1fr)",
        },
        columnGap: { xs: 1.25, sm: 1.5 },
        alignItems: "center",
        py: { xs: 0.75, sm: 0.625 },
        borderBottom: "1px solid rgba(22, 67, 135, 0.1)",
        "&:last-of-type": { borderBottom: "none", pb: 0 },
        "&:first-of-type": { pt: 0 },
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: "text.secondary",
          fontSize: { xs: "0.8125rem", sm: "0.875rem" },
          lineHeight: 1.35,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          color: "text.primary",
          fontSize: { xs: "0.875rem", sm: "0.9375rem" },
          lineHeight: 1.35,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
        }}
        title={value}
      >
        {value}
      </Typography>
    </Box>
  );

  const summaryCardSx = {
    border: "1px solid rgba(22, 67, 135, 0.2)",
    borderRadius: 2,
    bgcolor: THEME_PRIMARY_TINT,
    px: { xs: 1.5, sm: 1.75 },
    py: { xs: 1.25, sm: 1.125 },
    boxShadow: "0 1px 2px rgba(22, 67, 135, 0.06)",
    minWidth: 0,
    width: "100%",
  } as const;

  const summaryCardTitleSx = {
    display: "block",
    fontWeight: 700,
    color: THEME_PRIMARY,
    fontSize: "0.7rem",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    mb: 0.75,
    pb: 0.5,
    borderBottom: `2px solid ${THEME_PRIMARY}`,
  } as const;

  const vehicleForSummary: CurrentBookingVehicleSummary | null =
    currentBookingContext
      ? {
          vehicleName: currentBookingContext.vehicleName,
          vehicleNum: currentBookingContext.vehicleNum,
          registrationNumber: currentBookingContext.registrationNumber,
          imageUrl: currentBookingContext.imageUrl,
          speed: currentBookingContext.speed,
          range: currentBookingContext.range,
          hubName: currentBookingContext.hubName,
          modelName: currentBookingContext.modelName,
          currentPlanName: currentBookingContext.planName,
          bookingAmount: currentBookingContext.bookingAmount,
          planStartDate: currentBookingContext.planStartDate,
          planEndDate: currentBookingContext.planEndDate,
        }
      : null;

  useEffect(() => {
    if (awaitingCostBreakdown) return;
    if (paymentOptionsDisabled && paymentMethod !== "cash") {
      onPaymentMethodChange("cash");
      return;
    }
    if (!onlinePaymentEnabled && paymentMethod === "online") {
      onPaymentMethodChange("cash");
    }
  }, [
    awaitingCostBreakdown,
    paymentOptionsDisabled,
    onlinePaymentEnabled,
    paymentMethod,
    onPaymentMethodChange,
  ]);

  const [cashHandlingEditing, setCashHandlingEditing] = useState(false);
  const [cashHandlingDraft, setCashHandlingDraft] = useState("");

  useEffect(() => {
    if (paymentMethod !== "cash") setCashHandlingEditing(false);
  }, [paymentMethod]);

  useEffect(() => {
    if (costBreakdownLoading) setCashHandlingEditing(false);
  }, [costBreakdownLoading]);

  const openCashHandlingEdit = () => {
    const base = cashHandlingFees.trim() !== "" ? cashHandlingFees : "0";
    setCashHandlingDraft(base);
    setCashHandlingEditing(true);
  };

  const commitCashHandlingEdit = () => {
    const n = Number.parseFloat(String(cashHandlingDraft).trim());
    const v = Number.isFinite(n) && n >= 0 ? n : 0;
    onCashHandlingFeesChange(String(v));
    setCashHandlingEditing(false);
  };

  const cashHandlingEditButtonSx = {
    p: 0,
    minWidth: "auto",
    fontWeight: 600,
    fontSize: "0.8rem",
    textTransform: "none",
    color: "#0B35D6",
    textDecoration: "underline",
    "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
  } as const;

  const breakdownRows =
    paymentMethod === "online"
      ? costBreakdown.filter((item) => item.label !== CASH_HANDLING_LABEL)
      : costBreakdown;
  const [onboardingDialogOpen, setOnboardingDialogOpen] = useState(false);
  const [onboardingProcessingDraft, setOnboardingProcessingDraft] = useState(
    onboardingProcessingFees,
  );
  const [onboardingDepositDraft, setOnboardingDepositDraft] = useState(
    onboardingDepositAmount,
  );
  const [onboardingRemarksDraft, setOnboardingRemarksDraft] =
    useState(onboardingRemarks);
  const [onboardingRemarksError, setOnboardingRemarksError] = useState(false);
  /* TODO: Re-enable with Custom Plan card + dialog below.
  const [customPlanDialogOpen, setCustomPlanDialogOpen] = useState(false);
  const [customPlanFromDate, setCustomPlanFromDate] = useState("");
  const [customPlanToDate, setCustomPlanToDate] = useState("");
  const [customPlanDraftFrom, setCustomPlanDraftFrom] = useState<Dayjs | null>(null);
  const [customPlanDraftTo, setCustomPlanDraftTo] = useState<Dayjs | null>(null);
  const [customPlanDateError, setCustomPlanDateError] = useState<string | null>(null);
  */

  useEffect(() => {
    if (onboardingDialogOpen) return;
    setOnboardingProcessingDraft(onboardingProcessingFees);
    setOnboardingDepositDraft(onboardingDepositAmount);
    setOnboardingRemarksDraft(onboardingRemarks);
  }, [
    onboardingProcessingFees,
    onboardingDepositAmount,
    onboardingRemarks,
    onboardingDialogOpen,
  ]);

  const openOnboardingDialog = () => {
    setOnboardingProcessingDraft(onboardingProcessingFees);
    setOnboardingDepositDraft(onboardingDepositAmount);
    setOnboardingRemarksDraft(onboardingRemarks);
    setOnboardingRemarksError(false);
    setOnboardingDialogOpen(true);
  };

  const submitOnboardingDialog = () => {
    if (onboardingRemarksDraft.trim() === "") {
      setOnboardingRemarksError(true);
      return;
    }
    const p = Number.parseFloat(String(onboardingProcessingDraft).trim());
    const d = Number.parseFloat(String(onboardingDepositDraft).trim());
    const normalizedProcessing = Number.isFinite(p) && p >= 0 ? String(p) : "0";
    const normalizedDeposit = Number.isFinite(d) && d >= 0 ? String(d) : "0";
    setOnboardingRemarksError(false);
    onOnboardingFeeChange(
      normalizedProcessing,
      normalizedDeposit,
      onboardingRemarksDraft.trim(),
    );
    setOnboardingDialogOpen(false);
  };

  const baselineProcessingFees = parseOnboardingAmount(
    onboardingBaselineProcessingFees,
  );
  const baselineDepositAmount = parseOnboardingAmount(
    onboardingBaselineDepositAmount,
  );
  const currentProcessingFees = parseOnboardingAmount(onboardingProcessingFees);
  const currentDepositAmount = parseOnboardingAmount(onboardingDepositAmount);
  const baselineCashHandlingFees = parseOnboardingAmount(
    cashHandlingBaselineFees,
  );
  const currentCashHandlingFees = parseOnboardingAmount(cashHandlingFees);

  const getBreakdownDisplayLabel = (item: CostBreakdownItem): string => {
    if (item.label === ONBOARDING_PROCESSING_LABEL) {
      return getFeeDisplayLabel(
        ONBOARDING_PROCESSING_LABEL,
        WAIVED_PROCESSING_LABEL,
        baselineProcessingFees,
        currentProcessingFees,
      );
    }
    if (item.label === ONBOARDING_DEPOSIT_LABEL) {
      return getFeeDisplayLabel(
        ONBOARDING_DEPOSIT_LABEL,
        WAIVED_DEPOSIT_LABEL,
        baselineDepositAmount,
        currentDepositAmount,
      );
    }
    if (item.label === CASH_HANDLING_LABEL) {
      return getFeeDisplayLabel(
        CASH_HANDLING_LABEL,
        WAIVED_CASH_HANDLING_LABEL,
        baselineCashHandlingFees,
        currentCashHandlingFees,
      );
    }
    return item.label;
  };

  /* TODO: Re-enable with Custom Plan card + dialog.
  const openCustomPlanDialog = () => {
    setCustomPlanDateError(null);
    setCustomPlanDraftFrom(dayjs().startOf("day"));
    setCustomPlanDraftTo(null);
    setCustomPlanDialogOpen(true);
  };

  const submitCustomPlanDialog = () => {
    const from = customPlanDraftFrom;
    const to = customPlanDraftTo;
    if (!from || !to) {
      setCustomPlanDateError("Select both start and end dates.");
      return;
    }
    const todayStart = dayjs().startOf("day");
    const fromDay = from.startOf("day");
    if (!fromDay.isValid() || fromDay.isBefore(todayStart)) {
      setCustomPlanDateError("From Date cannot be in the past.");
      return;
    }
    if (!isCustomPlanValidToDate(from, to)) {
      setCustomPlanDateError("To Date must be exactly 7, 14, or 28 days after the start date.");
      return;
    }
    setCustomPlanDateError(null);
    setCustomPlanFromDate(fromDay.format("YYYY-MM-DD"));
    setCustomPlanToDate(to.startOf("day").format("YYYY-MM-DD"));
    setCustomPlanDialogOpen(false);
  };
  */

  let planGridContent: React.ReactNode;
  if (loading) {
    planGridContent = (
      <Typography color="text.secondary" sx={{ py: 2 }}>
        Loading plans...
      </Typography>
    );
  } else if (plans.length === 0) {
    planGridContent = (
      <Typography color="text.secondary" sx={{ py: 2 }}>
        No plans available for the selected vehicle model.
      </Typography>
    );
  } else {
    planGridContent = (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(4, 1fr)",
          },
          gap: { xs: 1.5, sm: 2 },
          mb: { xs: 2, sm: 3 },
        }}
      >
        {plans.map((plan) => {
          const isSelected =
            selectedPlanId !== null &&
            String(selectedPlanId) === String(plan.id);
          return (
            <Card
              key={plan.id}
              onClick={() => onSelectPlan(plan.id)}
              sx={{
                cursor: "pointer",
                border: "2px solid",
                borderColor: isSelected ? THEME_PRIMARY : "#e0e0e0",
                borderRadius: "16px",
                bgcolor: isSelected ? "rgba(22, 67, 135, 0.06)" : "#fff",
                boxShadow: "none",
                position: "relative",
                "&:hover": {
                  borderColor: isSelected ? THEME_PRIMARY : THEME_PRIMARY_LIGHT,
                  boxShadow: 1,
                },
              }}
            >
              {plan.badge && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background:
                      "linear-gradient(135deg, rgba(22, 67, 135, 0.9) 0%, rgba(94, 53, 177, 0.9) 100%)",
                    color: "#fff",
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1,
                  }}
                >
                  {plan.badge}
                </Box>
              )}
              <CardContent
                sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: 2 } }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: 1,
                    pr: plan.badge ? 3 : 0,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      lineHeight: 1.3,
                      fontSize: { xs: "0.95rem", sm: "1rem" },
                    }}
                  >
                    {plan.name}
                  </Typography>
                  {plan.duration && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.8125rem",
                        flexShrink: 0,
                      }}
                    >
                      {plan.duration}
                    </Typography>
                  )}
                </Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: THEME_PRIMARY,
                    fontSize: { xs: "1rem", sm: "1.125rem" },
                    lineHeight: 1.3,
                  }}
                >
                  {plan.price ?? "—"}
                </Typography>
              </CardContent>
            </Card>
          );
        })}
        {/*
        TODO: Re-enable Custom Plan (uncomment helpers/state/handlers at top of file + dialog below).
        <Card
          onClick={openCustomPlanDialog}
          sx={{
            cursor: "pointer",
            border: "2px solid #e0e0e0",
            borderRadius: "16px",
            bgcolor: "#fff",
            boxShadow: "none",
            "&:hover": { borderColor: THEME_PRIMARY_LIGHT, boxShadow: 1 },
          }}
        >
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: 2 } }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: "text.primary",
                lineHeight: 1.3,
                fontSize: { xs: "0.95rem", sm: "1rem" },
                mb: 1,
              }}
            >
              Custom Plan
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                color: THEME_PRIMARY,
                fontSize: { xs: "0.9rem", sm: "1rem" },
                lineHeight: 1.3,
              }}
            >
              {customPlanFromDate && customPlanToDate
                ? `${formatCustomPlanDateDisplay(customPlanFromDate)} to ${formatCustomPlanDateDisplay(customPlanToDate)}`
                : "Select Dates"}
            </Typography>
          </CardContent>
        </Card>
        */}
      </Box>
    );
  }

  const showSummaryRow = showYourSelection || showCurrentVehicle;

  return (
    <Box sx={{ mb: 1, minWidth: 0 }}>
      {showSummaryRow ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm:
                showYourSelection && showCurrentVehicle
                  ? "repeat(2, minmax(0, 1fr))"
                  : "1fr",
            },
            gap: { xs: 1.75, sm: 2 },
            width: "100%",
            maxWidth: {
              sm: showYourSelection && showCurrentVehicle ? 920 : 480,
            },
            mb: { xs: 2, sm: 2.5, md: 3 },
            alignItems: "stretch",
          }}
        >
          {showCurrentVehicle && vehicleForSummary ? (
            <CurrentVehicleSummaryCard vehicle={vehicleForSummary} />
          ) : null}
          {showYourSelection ? (
            <Box sx={summaryCardSx}>
              <Typography variant="caption" sx={summaryCardTitleSx}>
                Your selection
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {selectionSummaryRow(
                  "Hub",
                  formatCurrentVehicleDetailValue(hubLabel),
                )}
                {selectionSummaryRow(
                  "Model",
                  formatCurrentVehicleDetailValue(modelLabel),
                )}
                <YourSelectionVehicleDetailRows
                  selectionSummaryRow={selectionSummaryRow}
                  details={selectionVehicleDetails}
                />
                {selectionSummaryRow(
                  "Selected plan",
                  formatCurrentVehicleDetailValue(selectedPlanLabel),
                )}
                {selectionSummaryRow(
                  "New plan start",
                  formatCurrentVehicleDetailValue(
                    newPlanWindowDisplay?.start ?? "",
                  ),
                )}
                {selectionSummaryRow(
                  "New plan end",
                  formatCurrentVehicleDetailValue(
                    newPlanWindowDisplay?.end ?? "",
                  ),
                )}
              </Box>
            </Box>
          ) : null}
        </Box>
      ) : null}

      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: "text.primary",
          fontSize: { xs: 18, sm: 20 },
          lineHeight: 1.35,
          width: "100%",
          mb: showSummaryRow ? { xs: 1.5, sm: 1.75 } : { xs: 2, sm: 2.5 },
        }}
      >
        Select Plan
      </Typography>
      {planGridContent}

      <Box sx={{ mb: { xs: 2, sm: 2.5 }, width: "100%", maxWidth: 720 }}>
        <Typography
          variant="subtitle1"
          component="h3"
          sx={{
            fontWeight: 700,
            color: "text.primary",
            fontSize: { xs: "0.95rem", sm: "1rem" },
            mb: 3,
            lineHeight: 1.35,
          }}
        >
          Apply Coupon/Promo Code
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "flex-start" },
            gap: { xs: 1.5, sm: 1.25 },
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              width: { xs: "100%", sm: "32%" },
              maxWidth: { sm: "42%" },
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
                "& fieldset": { borderColor: "#dee2e6" },
              },
            }}
          >
            <SearchableSelectInput
              field="couponCode"
              formData={{ couponCode }}
              onChange={(_field, value) => onCouponChange(value)}
              options={promoCodeOptions}
              label="Coupon code"
              placeholder={
                couponSectionEnabled
                  ? "Search or select coupon"
                  : "Select a plan first"
              }
              loading={promoCodesLoading}
              disabled={!couponSectionEnabled || couponApplyLoading}
            />
          </Box>
          <Button
            type="button"
            variant="contained"
            disableElevation
            onClick={onApplyCoupon}
            disabled={!couponSectionEnabled || couponApplyLoading}
            sx={{
              flexShrink: 0,
              alignSelf: { xs: "stretch", sm: "center" },
              minHeight: 40,
              minWidth: { xs: "100%", sm: 100 },
              py: 1,
              px: 2.5,
              borderRadius: 1,
              backgroundColor: "#FFEB3B",
              color: "#164387",
              fontWeight: 700,
              fontSize: "0.9375rem",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { backgroundColor: "#FDD835", boxShadow: "none" },
            }}
          >
            Apply
          </Button>
        </Box>
      </Box>

      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              whiteSpace: "nowrap",
              fontSize: "1rem",
            }}
          >
            Additional Fees
          </Typography>
          <TextField
            size="small"
            value={additionalFees}
            onChange={(e) => onAdditionalFeesChange(e.target.value)}
            type="number"
            inputProps={{ min: 0, step: "any" }}
            sx={{
              width: { xs: "100%", sm: 120 },
              maxWidth: 140,
              "& .MuiOutlinedInput-root": { borderRadius: 1 },
            }}
          />
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}
        >
          Pay With
        </Typography>
        <RadioGroup
          row
          value={paymentMethod}
          onChange={(e) =>
            onPaymentMethodChange(e.target.value as "cash" | "online")
          }
        >
          <FormControlLabel
            value="cash"
            disabled={paymentOptionsDisabled}
            control={
              <Radio
                size="small"
                sx={{ color: THEME_PRIMARY }}
                disabled={paymentOptionsDisabled}
              />
            }
            label="Cash"
          />
          <FormControlLabel
            value="online"
            disabled={paymentOptionsDisabled || !onlinePaymentEnabled}
            control={
              <Radio
                size="small"
                sx={{ color: THEME_PRIMARY }}
                disabled={paymentOptionsDisabled || !onlinePaymentEnabled}
              />
            }
            label="Online"
          />
        </RadioGroup>
      </Box>

      <Box
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          p: { xs: 1.5, sm: 2 },
          bgcolor: "#fafafa",
          filter: paymentOptionsDisabled ? "blur(0.5px)" : "none",
          opacity: paymentOptionsDisabled ? 0.85 : 1,
          transition: "filter 160ms ease, opacity 160ms ease",
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            mb: 1.5,
            color: "text.primary",
            fontSize: { xs: "0.95rem", sm: "1rem" },
          }}
        >
          Cost Breakdown
        </Typography>
        {costBreakdownLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              py: { xs: 4, sm: 5 },
              px: 2,
              minHeight: 200,
            }}
          >
            <CircularProgress
              size={40}
              thickness={4}
              sx={{ color: THEME_PRIMARY }}
            />
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", textAlign: "center" }}
            >
              Loading cost breakdown…
            </Typography>
          </Box>
        ) : (
          <>
            {breakdownRows.map((item, index) => {
              const displayLabel = getBreakdownDisplayLabel(item);
              const rowBorderBottom =
                index < breakdownRows.length - 1 ||
                item.label === "Plan price"
                  ? "1px solid #eee"
                  : "none";

              const feePreviousAmount =
                item.label === ONBOARDING_PROCESSING_LABEL
                  ? baselineProcessingFees
                  : item.label === ONBOARDING_DEPOSIT_LABEL
                    ? baselineDepositAmount
                    : item.label === CASH_HANDLING_LABEL
                      ? baselineCashHandlingFees
                      : null;
              const feeCurrentAmount =
                item.label === ONBOARDING_PROCESSING_LABEL
                  ? currentProcessingFees
                  : item.label === ONBOARDING_DEPOSIT_LABEL
                    ? currentDepositAmount
                    : item.label === CASH_HANDLING_LABEL
                      ? currentCashHandlingFees
                      : null;
              const showFeePreviousAccordion =
                feePreviousAmount != null &&
                feeCurrentAmount != null &&
                shouldShowOnboardingPreviousRow(
                  feePreviousAmount,
                  feeCurrentAmount,
                );

              const onboardingFeeRow =
                item.label === "Plan price" ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 2,
                      py: 1,
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "text.primary" }}
                      >
                        Onboarding Fee
                      </Typography>
                      <Button
                        variant="text"
                        size="small"
                        onClick={openOnboardingDialog}
                        sx={{
                          p: 0,
                          minWidth: "auto",
                          fontWeight: 600,
                          fontSize: "0.8rem",
                          textTransform: "none",
                          color: "#0B35D6",
                          textDecoration: "underline",
                          "&:hover": {
                            bgcolor: "transparent",
                            textDecoration: "underline",
                          },
                        }}
                      >
                        Edit Amount
                      </Button>
                    </Box>
                    <Box sx={{ width: 90, flexShrink: 0 }} />
                  </Box>
                ) : null;

              if (showFeePreviousAccordion) {
                return (
                  <React.Fragment key={item.label + index}>
                    <Accordion
                      disableGutters
                      elevation={0}
                      sx={{
                        borderBottom: rowBorderBottom,
                        bgcolor: "transparent",
                        "&::before": { display: "none" },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ fontSize: 20 }} />}
                        sx={{
                          px: 0,
                          minHeight: 44,
                          "& .MuiAccordionSummary-content": {
                            my: 0.75,
                            alignItems: "center",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: 2,
                            width: "100%",
                            pr: 0.5,
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            {item.label === CASH_HANDLING_LABEL ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  flexWrap: "wrap",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    color: "text.primary",
                                  }}
                                >
                                  {displayLabel}
                                </Typography>
                                {cashHandlingEditing ? null : (
                                  <Button
                                    variant="text"
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openCashHandlingEdit();
                                    }}
                                    sx={cashHandlingEditButtonSx}
                                  >
                                    Edit
                                  </Button>
                                )}
                              </Box>
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, color: "text.primary" }}
                              >
                                {displayLabel}
                              </Typography>
                            )}
                            {item.message ? (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: "block",
                                  mt: 0.25,
                                  lineHeight: 1.4,
                                }}
                              >
                                {item.message}
                              </Typography>
                            ) : null}
                          </Box>
                          {item.label === CASH_HANDLING_LABEL &&
                          cashHandlingEditing ? (
                            <TextField
                              size="small"
                              value={cashHandlingDraft}
                              onChange={(e) =>
                                setCashHandlingDraft(e.target.value)
                              }
                              onBlur={commitCashHandlingEdit}
                              onKeyDown={(e) => {
                                e.stopPropagation();
                                if (e.key === "Enter")
                                  (e.target as HTMLInputElement).blur();
                              }}
                              onClick={(e) => e.stopPropagation()}
                              type="number"
                              inputProps={{ min: 0, step: "any" }}
                              autoFocus
                              sx={{
                                width: { xs: "100%", sm: 130 },
                                maxWidth: 160,
                                flexShrink: 0,
                                alignSelf: item.message ? "flex-start" : "center",
                                "& .MuiOutlinedInput-root": { borderRadius: 1 },
                              }}
                            />
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: item.isDiscount
                                  ? "success.main"
                                  : "text.primary",
                                flexShrink: 0,
                              }}
                            >
                              {displayCostBreakdownValue(item)}
                            </Typography>
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 0, pt: 0, pb: 1.5 }}>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          Previous:{" "}
                          <Box
                            component="span"
                            sx={{ fontWeight: 600, color: "text.primary" }}
                          >
                            {formatOnboardingRs(feePreviousAmount!)}
                          </Box>
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                    {onboardingFeeRow}
                  </React.Fragment>
                );
              }

              return (
                <React.Fragment key={item.label + index}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 2,
                      py: 1,
                      borderBottom: rowBorderBottom,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {item.label === CASH_HANDLING_LABEL ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: item.isDiscount ? 700 : 600,
                              color: item.isDiscount
                                ? "success.main"
                                : "text.primary",
                            }}
                          >
                            {displayLabel}
                          </Typography>
                          {cashHandlingEditing ? null : (
                            <Button
                              variant="text"
                              size="small"
                              onClick={openCashHandlingEdit}
                              sx={cashHandlingEditButtonSx}
                            >
                              Edit
                            </Button>
                          )}
                        </Box>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: item.isDiscount ? 700 : 600,
                            color: item.isDiscount
                              ? "success.main"
                              : "text.primary",
                          }}
                        >
                          {displayLabel}
                        </Typography>
                      )}
                      {item.message && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            display: "block",
                            mt: 0.25,
                            lineHeight: 1.4,
                          }}
                        >
                          {item.message}
                        </Typography>
                      )}
                    </Box>
                    {item.label === CASH_HANDLING_LABEL &&
                    cashHandlingEditing ? (
                      <TextField
                        size="small"
                        value={cashHandlingDraft}
                        onChange={(e) => setCashHandlingDraft(e.target.value)}
                        onBlur={commitCashHandlingEdit}
                        onKeyDown={(e) => {
                          if (e.key === "Enter")
                            (e.target as HTMLInputElement).blur();
                        }}
                        type="number"
                        inputProps={{ min: 0, step: "any" }}
                        autoFocus
                        sx={{
                          width: { xs: "100%", sm: 130 },
                          maxWidth: 160,
                          flexShrink: 0,
                          alignSelf: item.message ? "flex-start" : "center",
                          "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        }}
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: item.isDiscount
                            ? "success.main"
                            : "text.primary",
                          flexShrink: 0,
                          alignSelf: item.message ? "flex-start" : "center",
                        }}
                      >
                        {displayCostBreakdownValue(item)}
                      </Typography>
                    )}
                  </Box>
                  {onboardingFeeRow}
                </React.Fragment>
              );
            })}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 1.5,
                pt: 1.5,
                borderTop: "1px solid #e0e0e0",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: "text.primary" }}
              >
                Total Payable:
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  color:
                    paymentMethod === "online" ? THEME_PRIMARY : "text.primary",
                }}
              >
                {totalPayable}
              </Typography>
            </Box>
          </>
        )}
      </Box>

      <Dialog
        open={onboardingDialogOpen}
        onClose={() => setOnboardingDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            bgcolor: THEME_PRIMARY,
            color: "#fff",
            fontWeight: 700,
            py: 1.5,
            pr: 6,
          }}
        >
          Edit Onboarding Fee
          <IconButton
            onClick={() => setOnboardingDialogOpen(false)}
            sx={{ position: "absolute", right: 10, top: 8, color: "#fff" }}
            aria-label="close"
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: "20px !important", px: 3 }}>
          <Box
            sx={{
              border: "1px solid #dfe4ef",
              borderRadius: 2,
              p: { xs: 1.5, sm: 2 },
              bgcolor: "#fff",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: { xs: 1.25, sm: 2 },
                mb: 2,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    mb: 0.75,
                    color: "text.primary",
                    lineHeight: 1.2,
                  }}
                >
                  Processing Fees
                </Typography>
                <TextField
                  value={onboardingProcessingDraft}
                  onChange={(e) => setOnboardingProcessingDraft(e.target.value)}
                  type="number"
                  inputProps={{ min: 0, step: "any" }}
                  size="small"
                  fullWidth
                  placeholder="Enter processing fees"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.25 } }}
                />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    mb: 0.75,
                    color: "text.primary",
                    lineHeight: 1.2,
                  }}
                >
                  Deposit Amount
                </Typography>
                <TextField
                  value={onboardingDepositDraft}
                  onChange={(e) => setOnboardingDepositDraft(e.target.value)}
                  type="number"
                  inputProps={{ min: 0, step: "any" }}
                  size="small"
                  fullWidth
                  placeholder="Enter deposit amount"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.25 } }}
                />
              </Box>
            </Box>
            <Typography
              sx={{
                fontWeight: 600,
                mb: 0.75,
                color: "text.primary",
                lineHeight: 1.2,
              }}
            >
              Remarks
              <Box component="span" sx={{ color: "#d32f2f", ml: 0.25 }}>
                *
              </Box>
            </Typography>
            <TextField
              value={onboardingRemarksDraft}
              onChange={(e) => {
                setOnboardingRemarksDraft(e.target.value);
                if (onboardingRemarksError) setOnboardingRemarksError(false);
              }}
              multiline
              rows={3}
              fullWidth
              placeholder="Enter remarks"
              error={onboardingRemarksError}
              helperText={
                onboardingRemarksError ? "Remarks is required." : undefined
              }
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.25 } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setOnboardingDialogOpen(false)}
            sx={{
              color: THEME_PRIMARY,
              textTransform: "none",
              fontWeight: 600,
              minWidth: 90,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitOnboardingDialog}
            sx={{
              bgcolor: "#FFDE00",
              color: "#111",
              textTransform: "none",
              fontWeight: 700,
              minWidth: 100,
              "&:hover": { bgcolor: "#F3CF00" },
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/*
      TODO: Re-enable with Custom Plan card (see commented block in plan grid).
      <Dialog
        open={customPlanDialogOpen}
        onClose={() => setCustomPlanDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            bgcolor: THEME_PRIMARY,
            color: "#fff",
            fontWeight: 700,
            py: 1.5,
            pr: 6,
          }}
        >
          Select Dates
          <IconButton
            onClick={() => setCustomPlanDialogOpen(false)}
            sx={{ position: "absolute", right: 10, top: 8, color: "#fff" }}
            aria-label="close"
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: "20px !important", px: 3 }}>
          <Box sx={{ border: "1px solid #dfe4ef", borderRadius: 2, p: { xs: 1.5, sm: 2 }, bgcolor: "#fff" }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: { xs: 1.25, sm: 2 },
                  alignItems: "start",
                }}
              >
                <DatePicker
                  label="From Date"
                  value={customPlanDraftFrom}
                  onChange={(v) => {
                    setCustomPlanDraftFrom(v);
                    setCustomPlanDraftTo(null);
                    if (customPlanDateError) setCustomPlanDateError(null);
                  }}
                  minDate={dayjs().startOf("day")}
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                    },
                  }}
                />
                <DatePicker
                  label="To Date"
                  value={customPlanDraftTo}
                  onChange={(v) => {
                    setCustomPlanDraftTo(v);
                    if (customPlanDateError) setCustomPlanDateError(null);
                  }}
                  disabled={!customPlanDraftFrom}
                  shouldDisableDate={(date) => {
                    if (!customPlanDraftFrom) return true;
                    const allowed = customPlanAllowedToDates(customPlanDraftFrom);
                    return !allowed.some((a) => a.isSame(date, "day"));
                  }}
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      helperText: customPlanDraftFrom
                        ? "Only 7, 14, or 28 days after the start date can be selected."
                        : "Select a start date first.",
                    },
                  }}
                />
              </Box>
            </LocalizationProvider>
            {customPlanDateError ? (
              <Typography variant="caption" sx={{ color: "error.main", mt: 1, display: "block" }}>
                {customPlanDateError}
              </Typography>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setCustomPlanDialogOpen(false)}
            sx={{ color: THEME_PRIMARY, textTransform: "none", fontWeight: 600, minWidth: 90 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitCustomPlanDialog}
            sx={{
              bgcolor: "#FFDE00",
              color: "#111",
              textTransform: "none",
              fontWeight: 700,
              minWidth: 100,
              "&:hover": { bgcolor: "#F3CF00" },
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      */}
    </Box>
  );
};

export default SelectPlanStep;
